import config from '../config'

export interface TestDatasetProjectNode {
  id: number
  name: string
  parent_id?: number | null
  kind: 'project' | 'dataset'
  description?: string | null
  actions?: { view?: boolean; build?: boolean; download?: boolean; delete?: boolean; create?: boolean }
}

export interface TestDatasetJobStatus {
  status: string
  step?: string
  progress?: number
  download_url?: string
  error?: string
}

export interface TestDatasetSummary {
  job_id: number
  created_at: string
  expires_at?: string
  artifact_size?: number
  total: number
  label_set_id: number
  label_version_name?: string
  label_version_path?: string
  label_format?: string
  label_task_type?: string
  created_by_id?: number
  created_by_email?: string
  created_by_name?: string
  label_archive_name?: string
}

class TestDatasetService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async listProjects(): Promise<TestDatasetProjectNode[]> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/projects`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  async createProject(data: {
    projectName: string
    projectDescription?: string
    datasetName?: string
    datasetDescription?: string
    existingProjectId?: number
  }): Promise<{ project_id: number; dataset_id?: number }> {
    if (data.existingProjectId && data.datasetName) {
      const resp = await fetch(`${this.baseUrl}/test-datasets/projects/${data.existingProjectId}/datasets`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ dataset_name: data.datasetName, dataset_description: data.datasetDescription }),
      })
      if (!resp.ok) {
        const txt = await resp.text()
        throw new Error(txt || `데이터셋 생성 실패(${resp.status})`)
      }
      const r = await resp.json()
      return { project_id: data.existingProjectId, dataset_id: r.dataset_id }
    }
    if (data.existingProjectId && !data.datasetName) {
      return { project_id: data.existingProjectId }
    }
    const resp = await fetch(`${this.baseUrl}/test-datasets/projects`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        name: data.projectName,
        dataset_name: data.datasetName,
        project_description: data.projectDescription,
        dataset_description: data.datasetDescription,
      }),
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `프로젝트 생성 실패(${resp.status})`)
    }
    return resp.json()
  }

  async buildTestDataset(testNodeId: number, labelVersionNodeId: number): Promise<{ job_id?: number; ready?: boolean; download_url?: string }> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/build`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ test_node_id: testNodeId, label_version_node_id: labelVersionNodeId }),
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `테스트 데이터셋 생성 요청 실패(${resp.status})`)
    }
    return resp.json()
  }

  async getJobStatus(jobId: number): Promise<TestDatasetJobStatus> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/jobs/${jobId}/status`, { headers: this.getAuthHeaders() })
    if (!resp.ok) throw new Error(`상태 조회 실패(${resp.status})`)
    return resp.json()
  }

  async deleteJob(jobId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/jobs/${jobId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `삭제 실패(${resp.status})`)
    }
  }

  async getLatest(testNodeId: number): Promise<{ ready: boolean; expires_at?: string; download_url?: string; active_job?: { job_id: number; status: string; step?: string; progress?: number } }> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/${testNodeId}/latest`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return { ready: false }
    return resp.json()
  }

  async getSummary(testNodeId: number): Promise<TestDatasetSummary> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/${testNodeId}/summary`, { headers: this.getAuthHeaders() })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `요약 조회 실패(${resp.status})`)
    }
    return resp.json()
  }

  async download(testNodeId: number, preferredName?: string): Promise<void> {
    const qp = preferredName?.trim() ? `?filename=${encodeURIComponent(preferredName.trim())}` : ''
    const resp = await fetch(`${this.baseUrl}/test-datasets/${testNodeId}/download${qp}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
    })
    if (!resp.ok) throw new Error(`다운로드 실패(${resp.status})`)
    const blob = await resp.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const cd = resp.headers.get('Content-Disposition') || ''
    let filename = preferredName?.trim() || `test_dataset_${testNodeId}.zip`
    try {
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd)
      const raw = (m && (m[1] || m[2])) || ''
      if (raw) {
        const decoded = decodeURIComponent(raw)
        if (decoded.trim()) filename = decoded.trim()
      }
    } catch {}
    if (!/\.zip$/i.test(filename)) filename = `${filename}.zip`
    document.body.appendChild(a)
    a.download = filename.replace(/[\\/\0]/g, '')
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  async deleteNode(nodeId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/nodes/${nodeId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `삭제 실패(${resp.status})`)
    }
  }

  async deleteProject(projectId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/test-datasets/projects/${projectId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `삭제 실패(${resp.status})`)
    }
  }
}

export default new TestDatasetService()
